import * as React from "react";
import {
  useDispatch,
  useMappedState
} from "redux-react-hook";
import styled from "styled-components";
import { toast } from "react-toastify";

import { FilterParams } from "src/library/utils";
import {
  ActionButton,
  Button
} from "src/library/components/Button";
import { LikeToDo } from "src/library/components/LikeToDo";
import Table from "src/core/dashboard/components/widgets/table/Table";
import CustomerInfoSidebar from "src/core/customers/components/CustomerInfoSidebar";
import {
  InfoBarWrapper,
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { getDropTableColumns } from "src/core/shared/components/tables/dropsTableColumns";
import {
  ERPSeedless,
  ERPRJL
} from "src/utils/constants";
import {
  useDropsApi,
  IDrop
} from "src/core/drops/api/drops.api";
import { PaginatedParams } from "src/core/shared/api";
import { setDrop } from "src/core/drops/store/drops.actions";
import type { IStore } from "src/core/shared/store";
import { useCustomer } from "src/core/customers/hooks";
import { PageProps } from "src/pages/Router/routes";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import { DeactivateDropModal } from "src/core/drops/components/DeactivateDrop";
import { useDropActions } from "src/core/drops/hooks/useDropActions";
import { ITableInstance } from "src/library/components/Tables/Table";

interface UrlParams {
  customerId: string;
}

const CustomerDropsPageWrapper = styled.div``;

const defaultPagination: PaginatedParams<IDrop> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "name", "ASC" ] ]
};

/**
 * Main "Drops" View
 *
 * @returns
 */
const CustomerDropsPage: React.ReactFCWithChildren<PageProps<unknown, UrlParams>> = props => {
  const {
    history, match, terminology
  } = props;

  const customerId = match ? parseInt(match.params.customerId, 0) : 0;
  const dispatch = useDispatch();
  const TableRef = React.useRef<ITableInstance<IDrop>>(null);
  const { isLoading: isLoadingCustomer, customer } = useCustomer(customerId);
  const [ dropsResponse, getDrops ] = useDropsApi("LIST_DROPS_BY_CUSTOMER");
  const [ updateDropStatusResponse, updateDropStatus ] = useDropsApi("UPDATE_DROP_STATUS");
  const dropsData = dropsResponse.data;

  const {
    getDropActions, isLoading, deactivateModalState, setDeactivateModalState
  } = useDropActions();

  const { erp } = useMappedState(({ global }: IStore) => ({ erp: global.settings && global.settings.erp.name }));
  const checkPermission = useCheckUserPermission();

  const canCreateDrop = React.useMemo(() => {
    return checkPermission("create-branch");
  }, [ checkPermission ]);

  const CreateNewDropButton = (
    <Button onClick={() => history.push(`/admin/customers/${customerId}/drops/create`)}>
      {`Create new ${terminology.drop.singular.text}`}
    </Button>
  );

  const columns = React.useMemo(() => {
    return getDropTableColumns({
      actionButton: info => {
        return (
          <ActionButton
            key={`action-${info.row.original.id}`}
            title="Actions"
            items={getDropActions(info.row.original)}
            loading={isLoading}
          />
        );
      },
      terminology,
      selectedColumns: [
        "dropNumber",
        "name",
        "address",
        "postcode",
        "status",
        "lastOrderUpdate",
        "priceCode",
        "actions"
      ]
    });
  }, [
    getDropActions,
    terminology,
    isLoading
  ]);

  const onFetchData = React.useCallback(
    (params: {filters: FilterParams<IDrop>, pagination: PaginatedParams<IDrop>}) => {
      getDrops({
        customerId,
        pagination: params.pagination,
        filters: params.filters
      });
    },
    [ customerId, getDrops ]
  );

  /**
   * Handle update drop status api response
   */
  React.useEffect(() => {
    if (updateDropStatusResponse.data && TableRef.current) {
      TableRef.current.reloadTable();
      toast.success(updateDropStatusResponse.data.message);
    } else if (updateDropStatusResponse.error) {
      toast.error(updateDropStatusResponse.error.message);
    }
  }, [ updateDropStatusResponse.data, updateDropStatusResponse.error ]);

  React.useEffect(() => {
    dispatch(setDrop(null));
  }, [ dispatch ]);

  return (
    <Layout
      {...props}
      title={customer ? `${customer.name} - ${terminology.drop.plural.text}` : ""}
    >
      <MainContentWrapper fill>
        <CustomerDropsPageWrapper>
          {(erp === ERPSeedless || erp === ERPRJL) && canCreateDrop ? (
            <LikeToDo
              copy={`Setup a new ${terminology.drop.plural.text} for this ${terminology.customer.plural.text}.`}
              buttons={[ CreateNewDropButton ]}
            />
          ) : null}

          <Table
            ref={TableRef}
            mainAction={row => history.push(`/admin/customers/${customerId}/drops/${row.original.id}/orders`)}
            error={dropsResponse.error}
            tableOf={terminology.drop.plural.text}
            loading={dropsResponse.pending}
            data={dropsData ? dropsData.result : []}
            onFetchData={onFetchData}
            rowCount={dropsData?.count ?? 0}
            pageCount={dropsData?.pages ?? 0}
            defaultPagination={defaultPagination}
            columns={columns}
          />

        </CustomerDropsPageWrapper>
      </MainContentWrapper>

      <InfoBarWrapper>
        <CustomerInfoSidebar
          terminology={terminology}
          isLoading={isLoadingCustomer}
          data={customer}
        />
      </InfoBarWrapper>

      <DeactivateDropModal
        dropToDeactivate={deactivateModalState}
        onDismiss={() => setDeactivateModalState(null)}
        onSave={params => updateDropStatus(params)}
      />
    </Layout>
  );
};

export default CustomerDropsPage;
